import styles from './CampaignBar.module.css';
import { CampaignListRow, calculateCampaignBookingsTotals } from './../../helpers';
import useNonNullContext from '../../../../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../../../../contexts/OptionsContext';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import Skeleton from '../../../../../../ui/Skeleton/Skeleton';
import PlatformIcon from '../../../../../../ui/PlatformIcon/PlatformIcon';
import { range } from 'lodash';
import Badge from '../../../../../../ui/Badge/Badge';
import { CampaignStatusLabels } from '../../../../../../utils/constants';

type CampaignBarProps = {
    campaignData: CampaignListRow;
    loading: boolean;
};

const CampaignBar = ({ campaignData, loading }: CampaignBarProps) => {
    const navigate = useNavigate();
    const { currencies } = useNonNullContext(OptionsContext);
    const currencySymbol = currencies.find((curr) => curr.id === campaignData.currency)?.symbol;

    const { brief, totalCost, totalCreators, selectedPlatforms, firstPost, lastPost } = calculateCampaignBookingsTotals(
        campaignData.bookings
    );

    const totalCostDisplayValue = totalCost ? `${currencySymbol}${totalCost}` : '-';

    const status = CampaignStatusLabels[campaignData.status];

    const platformIcons = (
        <>
            {!selectedPlatforms.length ? (
                <p className={styles.statValue}>-</p>
            ) : (
                selectedPlatforms.map((platform) => (
                    <PlatformIcon
                        platform={platform}
                        containerClassName={styles.platformIconContainer}
                        iconClassName={styles.platformIcon}
                    />
                ))
            )}
        </>
    );

    return (
        <div className={styles.wrapper} onClick={() => navigate(`/client/campaigns/${campaignData.id}`)}>
            <div className={styles.details}>
                <span className={styles.name}>{campaignData.name}</span>
                <span className={styles.username}>{campaignData.client_user_user_name}</span>
                {loading ? (
                    <Skeleton containerClassName={styles.briefSkeletonContainer} className={styles.briefSkeleton} />
                ) : (
                    <span className={styles.brief}>{brief}</span>
                )}
            </div>
            <div className={styles.stats}>
                <div className={styles.statColumn}>
                    <span className={styles.statValue}>{loading ? <Skeleton /> : totalCostDisplayValue}</span>
                    <span className={styles.statLabel}>{loading ? <Skeleton /> : 'Total Cost'}</span>
                </div>
                <div className={styles.statColumn}>
                    <span className={styles.statValue}>{loading ? <Skeleton /> : totalCreators}</span>
                    <span className={styles.statLabel}>{loading ? <Skeleton /> : 'Creators'}</span>
                </div>
                <div className={styles.statColumn}>
                    <div className={styles.row}>
                        {loading ? range(2).map((i) => <PlatformIcon loading platform={null} />) : platformIcons}
                    </div>
                    <span className={styles.statLabel}>{loading ? <Skeleton /> : 'Platforms'}</span>
                </div>
                <div className={cn(styles.statColumn, styles.postTimes)}>
                    <div className={styles.row}>
                        {loading ? (
                            <Skeleton containerClassName={styles.postTimesSkeleton} />
                        ) : (
                            <>
                                <p className={styles.statLabel}>First Post</p>
                                <p className={styles.smallValue}>{firstPost}</p>
                            </>
                        )}
                    </div>
                    <div className={styles.row}>
                        {loading ? (
                            <Skeleton containerClassName={styles.postTimesSkeleton} />
                        ) : (
                            <>
                                <p className={styles.statLabel}>Last Post</p>
                                <p className={styles.smallValue}>{lastPost}</p>
                            </>
                        )}
                    </div>
                </div>
                <div className={styles.statusWrapper}>
                    <Badge className={cn(styles.statusBadge, styles[campaignData.status])}>
                        <span>{status}</span>
                    </Badge>
                </div>
            </div>
        </div>
    );
};

export default CampaignBar;
